import { useMemo } from "react"
import Box from "@material-ui/core/Box"
import { Bar } from "react-chartjs-2"
import { Chart as ChartJS, BarElement, CategoryScale, LinearScale, Tooltip } from "chart.js"
import { ProjectsDashboardViewModel } from "../../api/projects-dashboard"
import { COLOR_WHITE, STATUS_COLORS } from "../../../constants"
import { useIsSmallScreen } from "../../../hooks/use-is-small-screen"
import { useTranslations } from "./useTranslations"
import { getOrCreateTooltip } from "../ProjectDashboard/util"

ChartJS.register(BarElement, CategoryScale, LinearScale, Tooltip)

const CompletedProjectsBarGraph = ({ projectsDashboard }: CompletedProjectsBarGraphProps) => {
  const translations = useTranslations()
  const isSmallScreen = useIsSmallScreen("xs")
  const { completedProjectsBarGraphData } = projectsDashboard
  const data = useMemo(() => {
    const labels = completedProjectsBarGraphData.map((item) => item.period)
    const datasets = [
      {
        label: "Completed Projects",
        data: completedProjectsBarGraphData.map((item) => item.numberOfProjects),
        backgroundColor: STATUS_COLORS.COMPLETED[1],
      },
    ]
    return { labels, datasets }
  }, [completedProjectsBarGraphData])

  let xScaleText = translations.monthlyScaleLabel
  if (completedProjectsBarGraphData[0]?.type === "week") {
    xScaleText = translations.weeklyScaleLabel
  }
  if (completedProjectsBarGraphData[0]?.type === "day") {
    xScaleText = translations.dailyScaleLabel
  }
  const yScalText = translations.numberOfProjectsLabel

  const options = {
    responsive: true,
    plugins: {
      legend: { display: false },
      tooltip: {
        enabled: false,
        position: "nearest" as const,
        external: (context: any) => {
          const { chart, tooltip } = context
          const tooltipEl = getOrCreateTooltip(chart)

          if (tooltip.opacity === 0) {
            tooltipEl.style.opacity = "0"
            return
          }

          if (tooltip.body) {
            const idx = tooltip.dataPoints[0].dataIndex
            const data = completedProjectsBarGraphData[idx]
            const projects = data.projects || []
            if (projects.length === 0) {
              tooltipEl.style.opacity = "0"
              return
            }
            let innerHtml = ""
            projects.forEach((project: string) => {
              innerHtml += `<div>• ${project}</div>`
            })
            tooltipEl.innerHTML = innerHtml
            tooltipEl.style.maxHeight = isSmallScreen ? "200px" : "400px"

            const { offsetLeft: positionX, offsetTop: positionY } = chart.canvas

            // Display, position, and set styles for font
            tooltipEl.style.opacity = 1
            tooltipEl.style.left = positionX + tooltip.caretX + "px"
            tooltipEl.style.top = positionY + tooltip.caretY + "px"
          }
        },
      },

      datalabels: {
        color: COLOR_WHITE,
        font: {
          weight: "bold" as const,
        },
        display: (context: any) => context.dataset.data[context.dataIndex] !== 0,
        formatter: (value: number) => (value === 0 ? "" : value),
      },
    },
    scales: {
      x: { title: { display: !isSmallScreen, text: xScaleText } },
      y: { title: { display: !isSmallScreen, text: yScalText }, beginAtZero: true },
    },
  }

  return (
    <Box>
      <Bar data={data} options={options} />
    </Box>
  )
}

export default CompletedProjectsBarGraph
type CompletedProjectsBarGraphProps = {
  projectsDashboard: ProjectsDashboardViewModel
}
